// frontend/app/utils/davinciModels.js
//
// 3D-model snapshot pipeline for the Da Vinci certificate + Certificate Wall.
//
// model-viewer (@google/model-viewer, loaded from unpkg) shares ONE WebGL
// renderer process-wide. Snapshotting several viewers concurrently bleeds the
// same image across them (the heart was showing up under "Chemistry"), so every
// capture MUST be serialized — captureModelImage() runs through a module-level
// promise-chain mutex.
//
// Because the wall can show dozens of certificates, we also cache each model's
// PNG so a given .glb is only rendered to a data URL ONCE per page load
// (getModelImageCached, backed by a Map with in-flight de-duping). prewarmAllModels()
// snapshots every distinct model up front so the wall is instant.
//
// Web-only (react-native-web): needs window/document + WebGL. Everything here
// guards typeof window/document and degrades to null off the web.

import { MODELS, MODEL_VIEWER_SRC, resolveUri, subjectToModel } from "./davinciCertHtml";

const isWeb = () => typeof window !== "undefined" && typeof document !== "undefined";

// ---------------------------------------------------------------------------
// model-viewer script loader
// ---------------------------------------------------------------------------

export const ensureModelViewer = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.customElements?.get("model-viewer")) return resolve(true);
    const existing = document.getElementById("dv-model-viewer-script");
    if (existing) {
      const t = setInterval(() => {
        if (window.customElements?.get("model-viewer")) {
          clearInterval(t);
          resolve(true);
        }
      }, 200);
      setTimeout(() => {
        clearInterval(t);
        resolve(!!window.customElements?.get("model-viewer"));
      }, 8000);
      return;
    }
    const script = document.createElement("script");
    script.id = "dv-model-viewer-script";
    script.type = "module";
    script.src = MODEL_VIEWER_SRC;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

// ---------------------------------------------------------------------------
// Serialized single-model capture (mutex)
// ---------------------------------------------------------------------------

// Promise-chain mutex: every capture appends to this chain, so concurrent
// callers run strictly one-at-a-time and never share the WebGL renderer.
let captureChain = Promise.resolve();

// Render ONE model-viewer in a temporary host that stays on-screen (model-viewer
// pauses rendering when off-screen/hidden), snapshot it, then tear the host down.
const captureOne = async (modelKey) => {
  if (!isWeb()) return null;
  const uri = resolveUri(MODELS[modelKey]);
  if (!uri) return null;

  const ok = await ensureModelViewer();
  if (!ok) return null;

  // On-screen but unobtrusive: fixed, tiny, near-transparent, no pointer/layout
  // impact. It must actually render, so we can't use display:none/visibility:hidden.
  const host = document.createElement("div");
  host.setAttribute("data-dv-capture", "1");
  host.style.cssText =
    "position:fixed;left:0;bottom:0;width:140px;height:140px;opacity:0.01;" +
    "pointer-events:none;z-index:-1;overflow:hidden;";
  host.innerHTML = `<model-viewer src="${uri}"
    auto-rotate autoplay rotation-per-second="26deg" interaction-prompt="none"
    camera-orbit="0deg 75deg auto" shadow-intensity="0.6" exposure="1.1"
    environment-image="neutral" loading="eager" reveal="auto" ar="false"
    style="width:140px;height:140px;background:transparent"></model-viewer>`;
  document.body.appendChild(host);

  try {
    const mv = host.querySelector("model-viewer");
    if (!mv) return null;
    await new Promise((res) => {
      if (mv.loaded) return res();
      mv.addEventListener("load", res, { once: true });
      setTimeout(res, 7000);
    });
    // Let it paint a couple of frames before reading the canvas.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      return mv.toDataURL("image/png");
    } catch {
      return null;
    }
  } finally {
    try {
      host.remove();
    } catch {
      /* host already detached */
    }
  }
};

// Public: serialized capture. Returns a dataURL or null. Concurrent calls queue.
export const captureModelImage = (modelKey) => {
  const run = captureChain.then(() => captureOne(modelKey));
  // Keep the chain alive even if a capture rejects, so one failure can't wedge
  // the queue for every later caller.
  captureChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
};

// ---------------------------------------------------------------------------
// Per-page cache (snapshot each model once)
// ---------------------------------------------------------------------------

// modelKey -> Promise<dataURL|null>. Storing the promise (not the resolved
// value) de-dupes in-flight callers asking for the same key.
const cache = new Map();

export const getModelImageCached = (modelKey) => {
  if (!modelKey) return Promise.resolve(null);
  if (cache.has(modelKey)) return cache.get(modelKey);
  const p = captureModelImage(modelKey).catch(() => null);
  cache.set(modelKey, p);
  return p;
};

// ---------------------------------------------------------------------------
// Subject -> [{url, label}] (shared by the certificate and the wall)
// ---------------------------------------------------------------------------

export const getModelImgsForSubjects = async (subjects) => {
  const list = Array.isArray(subjects) ? subjects.filter(Boolean) : [];
  const seen = new Set();
  const plan = [];
  for (const subj of list) {
    const m = subjectToModel(subj);
    if (seen.has(m.key)) continue;
    seen.add(m.key);
    plan.push(m);
  }
  const out = [];
  for (const m of plan) {
    const url = await getModelImageCached(m.key);
    if (url) out.push({ url, label: m.label });
  }
  return out;
};

// ---------------------------------------------------------------------------
// Prewarm — snapshot every distinct model once (sequentially)
// ---------------------------------------------------------------------------

export const prewarmAllModels = async () => {
  if (!isWeb()) return;
  for (const key of Object.keys(MODELS)) {
    // getModelImageCached serializes via the mutex and caches, so this is a
    // no-op for any model already warmed.
    await getModelImageCached(key);
  }
};
